import * as Api from '@ViewModels';
import * as React from 'react';
import { AppState } from '../../models/AppState';
import { IRealtimeTranscriptionUtteranceEvent } from '../../viewModels/phonecall';
import { IWebsocketSubscription } from '../../viewModels/websockets';
import { useTysonCoaching } from '../contexts/TysonCoachingContext';
import { TranscriptEntry } from '../types';
import { LiveUtterance, PartialUtterance } from '../types/coaching';

interface UseRealCallCoachingProps {
	phoneCallId: string | null;
	enabled: boolean;
}

interface UseRealCallCoachingReturn {
	liveUtterances: LiveUtterance[];
	flushAndGetEntries: () => TranscriptEntry[]; // RENAMED and returns entries
	transcriptCount: number;
	isActive: boolean;
}

// Stability threshold: >= 0.5 is stable, < 0.5 is speculative
const STABILITY_THRESHOLD = 0.5;

/**
 * Hook that bridges real-time Twilio transcription events to the Tyson coaching system.
 * Subscribes to RealtimeTranscriptionUtteranceEvent WebSocket events and tracks
 * live utterances for display. Call flushAndGetEntries() to get pending entries with
 * confidence markers for sending to coaching.
 *
 * Handles:
 * - Out-of-order events (rejects events with lower sequenceId than already seen)
 * - Stability pairs (routes to stableText or speculativeText based on threshold)
 * - Track-based keying (one partial utterance per speaker/track)
 * - Two-collection approach: completedUtterances + partialsByTrack
 * - Confidence markers: stable text is normal, speculative text gets <low_confidence> wrapper
 */
export const useRealCallCoaching = ({ phoneCallId, enabled }: UseRealCallCoachingProps): UseRealCallCoachingReturn => {
	const coaching = useTysonCoaching();
	const { websockets } = AppState;
	const [transcriptCount, setTranscriptCount] = React.useState(0);

	// Extract stable references to avoid dependency issues
	const coachingIsActive = coaching.isActive;

	// Two-collection approach:
	// 1. completedUtterances: Final utterances ready to send to AI
	// 2. partialsByTrack: Current partial utterance per track (speaker)
	const completedUtterancesRef = React.useRef<TranscriptEntry[]>([]);
	const partialsByTrackRef = React.useRef<Map<string, PartialUtterance>>(new Map());

	// Track highest sequenceId seen per track for out-of-order rejection
	const highestSeqByTrackRef = React.useRef<Map<string, number>>(new Map());

	// State for UI updates (derived from both collections)
	const [liveUtterancesVersion, setLiveUtterancesVersion] = React.useState(0);

	const flushAndGetEntries = React.useCallback((): TranscriptEntry[] => {
		const entries: TranscriptEntry[] = [];

		// 1. Add completed utterances (already final - full confidence)
		completedUtterancesRef.current.forEach(e => entries.push(e));

		// 2. Add current partials with confidence markers
		partialsByTrackRef.current.forEach(partial => {
			if (partial.stableText.trim() || partial.speculativeText.trim()) {
				let message = '';

				if (partial.stableText.trim()) {
					message += partial.stableText.trim();
				}

				if (partial.speculativeText.trim()) {
					message += ` <low_confidence>${partial.speculativeText.trim()}</low_confidence>`;
				}

				entries.push({
					speaker: partial.speaker,
					message: message.trim(),
					timestamp: partial.timestamp,
				});
			}
		});

		// 3. Clear completed utterances - partials stay for continued listening
		completedUtterancesRef.current = [];

		// 4. Trigger UI update
		setLiveUtterancesVersion(v => v + 1);

		return entries;
	}, []);

	React.useEffect(() => {
		if (!enabled || !phoneCallId || !coachingIsActive) {
			return;
		}

		const handleTranscriptionEvent = (events: Api.IRemoteEvent<IRealtimeTranscriptionUtteranceEvent>[]) => {
			for (const event of events) {
				const utterance = event.value;
				const seqId = parseInt(utterance.sequenceId, 10);
				const stability = parseFloat(utterance.stability || '1');
				const track = utterance.speaker; // 'inbound_track' or 'outbound_track'
				const speaker: 'Lead' | 'Sales Rep' = track === 'inbound_track' ? 'Lead' : 'Sales Rep';

				// Only process events for the current call
				if (utterance.phoneCallId !== phoneCallId) {
					continue;
				}

				// Out-of-order rejection: check if we've seen a higher sequenceId for this track
				const highestSeen = highestSeqByTrackRef.current.get(track) || 0;
				if (seqId < highestSeen) {
					continue;
				}
				highestSeqByTrackRef.current.set(track, seqId);

				// Get or create partial utterance for this track
				let partial = partialsByTrackRef.current.get(track);
				if (!partial) {
					partial = {
						sequenceId: seqId,
						speaker,
						stableText: '',
						speculativeText: '',
						final: false,
						timestamp: new Date(),
						highestSeqSeen: seqId,
					};
					partialsByTrackRef.current.set(track, partial);
				}

				// Update the partial based on stability
				partial.sequenceId = seqId;
				partial.highestSeqSeen = seqId;
				partial.timestamp = new Date();

				if (stability >= STABILITY_THRESHOLD) {
					// Stable text - this is confirmed
					partial.stableText = utterance.transcription;
					// Clear speculative when we get stable update
					partial.speculativeText = '';
				} else {
					// Speculative text - may change
					partial.speculativeText = utterance.transcription;
				}

				// Handle final utterance
				if (utterance.final) {
					partial.final = true;

					// Create completed entry with full text
					const fullText = partial.stableText || utterance.transcription;
					if (fullText.trim()) {
						const entry: TranscriptEntry = {
							speaker,
							message: fullText.trim(),
							timestamp: partial.timestamp,
						};
						completedUtterancesRef.current = [...completedUtterancesRef.current, entry];
					}

					// Clear partial for this track - ready for next utterance
					partialsByTrackRef.current.delete(track);
				}

				setTranscriptCount(prev => prev + 1);
				setLiveUtterancesVersion(v => v + 1);
			}
		};

		const subscription: IWebsocketSubscription<IRealtimeTranscriptionUtteranceEvent> = {
			callback: handleTranscriptionEvent,
			events: ['RealtimeTranscriptionUtteranceEvent'],
			name: 'realCallCoaching',
		};

		websockets.subscribe(subscription);

		return () => {
			websockets.unsubscribe('realCallCoaching');
		};
	}, [enabled, phoneCallId, coachingIsActive, websockets]);

	// Reset state when call ends or hook disables
	React.useEffect(() => {
		if (!enabled) {
			completedUtterancesRef.current = [];
			partialsByTrackRef.current = new Map();
			highestSeqByTrackRef.current = new Map();
			setLiveUtterancesVersion(0);
		}
	}, [enabled]);

	// Derive live utterances for display from both collections
	const liveUtterances = React.useMemo(() => {
		const utterances: LiveUtterance[] = [];

		// Add completed utterances (these are final)
		completedUtterancesRef.current.forEach((entry, index) => {
			utterances.push({
				sequenceId: `completed-${index}`,
				speaker: entry.speaker as 'Lead' | 'Sales Rep',
				transcription: entry.message,
				stableText: entry.message,
				speculativeText: '',
				final: true,
				timestamp: entry.timestamp,
			});
		});

		// Add current partials (these are in-progress)
		partialsByTrackRef.current.forEach(partial => {
			const combinedText = partial.stableText + (partial.speculativeText ? partial.speculativeText : '');
			utterances.push({
				sequenceId: `partial-${partial.speaker}-${partial.sequenceId}`,
				speaker: partial.speaker,
				transcription: combinedText,
				stableText: partial.stableText,
				speculativeText: partial.speculativeText,
				final: partial.final,
				timestamp: partial.timestamp,
			});
		});

		// Sort by timestamp
		return utterances.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [liveUtterancesVersion]);

	return {
		liveUtterances,
		flushAndGetEntries, // RENAMED
		transcriptCount,
		isActive: enabled && !!phoneCallId && coaching.isActive,
	};
};
